/**
 * @fileoverview Supabase client configuration for authentication and database operations
 * @module lib/supabase
 */

import { createClient } from '@supabase/supabase-js';

/**
 * Supabase project URL from environment variables.
 * Must be prefixed with VITE_ for client-side access.
 */
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

/**
 * Supabase anonymous API key from environment variables.
 * This key allows read access and user registration/authentication.
 */
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local'
  );
}

/**
 * Supabase client instance configured for the Onicorn application.
 *
 * Features enabled:
 * - Auto refresh tokens for seamless user experience
 * - Persistent sessions across browser refreshes
 * - Automatic session detection from URL for OAuth callbacks
 *
 * Used for:
 * - User authentication (social OAuth and email/password)
 * - Database operations with Row Level Security (RLS)
 * - Real-time subscriptions for live data updates
 *
 * @example
 * ```typescript
 * // Authentication
 * const { data, error } = await supabase.auth.signInWithOAuth({
 *   provider: 'google'
 * });
 *
 * // Database operations
 * const { data, error } = await supabase
 *   .from('profiles')
 *   .select('*')
 *   .eq('user_id', userId);
 * ```
 */
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

/**
 * Type definitions for the Supabase database schema.
 * Will be auto-generated or manually defined based on database structure.
 */
export type Database = {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string;
          user_id: string;
          user_type: 'regular' | 'kol' | 'project_creator' | 'kol_manager';
          email: string | null;
          full_name: string | null;
          avatar_url: string | null;
          auth_provider: 'wallet' | 'google' | 'twitter';
          wallet_address: string | null;
          onboarding_completed: boolean;
          onboarding_step: string;
          x_verified: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          user_type: 'regular' | 'kol' | 'project_creator' | 'kol_manager';
          email?: string | null;
          full_name?: string | null;
          avatar_url?: string | null;
          auth_provider: 'wallet' | 'google' | 'twitter';
          wallet_address?: string | null;
          onboarding_completed?: boolean;
          onboarding_step?: string;
          x_verified?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          user_type?: 'regular' | 'kol' | 'project_creator' | 'kol_manager';
          email?: string | null;
          full_name?: string | null;
          avatar_url?: string | null;
          auth_provider?: 'wallet' | 'google' | 'twitter';
          wallet_address?: string | null;
          onboarding_completed?: boolean;
          onboarding_step?: string;
          x_verified?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      user_wallets: {
        Row: {
          id: string;
          user_id: string;
          wallet_address: string;
          wallet_type: string;
          is_primary: boolean;
          created_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          wallet_address: string;
          wallet_type: string;
          is_primary?: boolean;
          created_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          wallet_address?: string;
          wallet_type?: string;
          is_primary?: boolean;
          created_at?: string;
        };
      };
      kol_profiles: {
        Row: {
          id: string;
          user_id: string;
          twitter_handle: string | null;
          youtube_channel: string | null;
          telegram_channel: string | null;
          referral_code: string | null;
          follower_count: number | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          twitter_handle?: string | null;
          youtube_channel?: string | null;
          telegram_channel?: string | null;
          referral_code?: string | null;
          follower_count?: number | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          twitter_handle?: string | null;
          youtube_channel?: string | null;
          telegram_channel?: string | null;
          referral_code?: string | null;
          follower_count?: number | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      project_profiles: {
        Row: {
          id: string;
          user_id: string;
          project_name: string | null;
          project_description: string | null;
          website_url: string | null;
          whitepaper_url: string | null;
          token_symbol: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          project_name?: string | null;
          project_description?: string | null;
          website_url?: string | null;
          whitepaper_url?: string | null;
          token_symbol?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          project_name?: string | null;
          project_description?: string | null;
          website_url?: string | null;
          whitepaper_url?: string | null;
          token_symbol?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      project_tickets: {
        Row: {
          id: string;
          user_id: string;
          project_name: string;
          token_symbol: string;
          blockchain: string;
          website_url: string;
          twitter_handle: string;
          telegram_group: string;
          short_description: string;
          backers_advisors: string | null;
          token_price: number;
          token_vesting: string;
          fdv_at_sale: number;
          softcap: number;
          hardcap: number;
          target_cex: string;
          listing_date: string;
          listing_price: number;
          fdv_at_listing: number;
          circulating_supply: number;
          contact_email: string;
          telegram_username: string;
          status: 'pending' | 'approved' | 'denied';
          submitted_at: string;
          reviewed_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          project_name: string;
          token_symbol: string;
          blockchain: string;
          website_url: string;
          twitter_handle: string;
          telegram_group: string;
          short_description: string;
          backers_advisors?: string | null;
          token_price: number;
          token_vesting: string;
          fdv_at_sale: number;
          softcap: number;
          hardcap: number;
          target_cex: string;
          listing_date: string;
          listing_price: number;
          fdv_at_listing: number;
          circulating_supply: number;
          contact_email: string;
          telegram_username: string;
          status?: 'pending' | 'approved' | 'denied';
          submitted_at?: string;
          reviewed_at?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          project_name?: string;
          token_symbol?: string;
          blockchain?: string;
          website_url?: string;
          twitter_handle?: string;
          telegram_group?: string;
          short_description?: string;
          backers_advisors?: string | null;
          token_price?: number;
          token_vesting?: string;
          fdv_at_sale?: number;
          softcap?: number;
          hardcap?: number;
          target_cex?: string;
          listing_date?: string;
          listing_price?: number;
          fdv_at_listing?: number;
          circulating_supply?: number;
          contact_email?: string;
          telegram_username?: string;
          status?: 'pending' | 'approved' | 'denied';
          submitted_at?: string;
          reviewed_at?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
    };
  };
};